import React, { useEffect, useMemo } from 'react';
import { RouterProvider, createBrowserRouter, RouteObject } from "react-router-dom";
import { IntlProvider } from "react-intl";
import { useAtomValue } from "jotai";
import styled from "styled-components";
import { Toaster } from "react-hot-toast";
import ko from "@/i18n/ko";
import en from "@/i18n/en";
import { NLU_CTX } from "@/core/variables";
import { DictType } from "@/model";
import { useProfile } from "@/query/profile";
import localeAtom from "@/atom/locale";
import { useRouteHandler } from "@/hook";
import { Dialog, JSONViewer } from "@/component/modal";
import { Error404View } from "@/component/error/ErrorView";
import ErrorBoundaryLayout from "@/component/error/ErrorBoundaryLayout";
import AgentContextView from '@/view/AgentContextView';
import AgentListView from "@/view/agents/AgentListView";


const RootView: React.FC = () => {
    const { resetRoute } = useRouteHandler();

    useEffect(() => {
        resetRoute();
    }, [resetRoute]);

    return null;
};

const RouterInfo: RouteObject[] = [
    {
        path: "/",
        element: <ErrorBoundaryLayout />,
        children: [
            {
                path: "",
                element: <RootView />
            },
            {
                path: "/agents",
                element: <AgentListView />
            },
            {
                path: "/agents/:agentId/*",
                element: <AgentContextView />
            },
            {
                path: "*",
                element: <Error404View />
            }
        ]
    }
];

const RouterObject = createBrowserRouter(
    RouterInfo,
    {
        basename: NLU_CTX || ""
    }
);

const AppWrapper: React.FC = () => {
    const locale = useAtomValue(localeAtom);

    const messages: DictType<Record<string, string>> = useMemo(() => {
        return { ko: ko, en: en };
    }, []);

    // @ts-ignore
    return (<IntlProvider locale={locale} messages={messages[locale]}>
                <App />
            </IntlProvider>);
};

const App: React.FC = () => {
    const { isPending } = useProfile();
    if (isPending) {
        return null;
    }
    return (
        <div className="scrollbar-track-transparent scrollbar-thumb-black-200 scrollbar-thumb-rounded-full scrollbar-thumb-w-[6px]">
            <AppStyle>
              <RouterProvider router={RouterObject} />
            </AppStyle>
            <Toaster position="top-right"
                     containerStyle={{ top: "15px" }}
                     toastOptions={{
                         style: {
                             maxWidth: "800px"
                         },
                     }}
            />
            <Dialog />
            <JSONViewer />
        </div>
    );
};

const AppStyle = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  box-sizing: border-box;
`;

export default AppWrapper;